import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { firebaseAuth } from "../lib/firebase";
import { getTrialStatus, cancelSubscription } from "../services/paymentService";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { Checkbox } from "../components/ui/checkbox";
import { HEALTH_CONDITIONS, BMI_MIN, BMI_MAX, SUBSCRIPTION_DISPLAY_PRICE } from "../lib/constants";
import {
  MessageCircle,
  Phone,
  RefreshCw,
  LogOut,
  Settings,
  Utensils,
  Target,
  Clock,
  CheckCircle2,
  Calendar,
  Plus,
  Loader2,
  ChevronRight,
  ChevronDown,
  History,
  PhoneCall,
  PhoneOff,
  Flame,
  Weight,
  Mic,
  Pencil,
  Trash2,
  X,
  Save,
  UserCog,
  AlertTriangle,
  CreditCard,
  Camera,
  Upload,
  ImageIcon,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [dietPlan, setDietPlan] = useState(null);
  const [callSchedule, setCallSchedule] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const [todayNutrition, setTodayNutrition] = useState([]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isLoggingFood, setIsLoggingFood] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [customCondition, setCustomCondition] = useState("");
  const [regeneratePlan, setRegeneratePlan] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [newCallTime, setNewCallTime] = useState("09:00");
  const [newCallFrequency, setNewCallFrequency] = useState("daily");

  // Core Feature state
  const [nutritionSummary, setNutritionSummary] = useState(null);
  const [streak, setStreak] = useState(0);
  const [weightHistory, setWeightHistory] = useState([]);
  const [weightInput, setWeightInput] = useState("");
  const [isLoggingWeight, setIsLoggingWeight] = useState(false);
  const [expandedCall, setExpandedCall] = useState(null);
  const [weeklyNutrition, setWeeklyNutrition] = useState(null);
  const [isEstimatingCalories, setIsEstimatingCalories] = useState(false);

  // Edit / delete log state
  const [editingLogId, setEditingLogId] = useState(null);
  const [editForm, setEditForm] = useState({ meal_type: "", food_items: "", calories: "", notes: "" });
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [deletingLogId, setDeletingLogId] = useState(null);

  // Food logging form
  const [foodForm, setFoodForm] = useState({
    meal_type: "breakfast",
    food_items: "",
    calories: "",
    protein_g: "",
    carbs_g: "",
    fat_g: "",
    notes: "",
  });

  const [goalProjection, setGoalProjection] = useState(null);

  // Image upload state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const imageInputRef = useRef(null);


  // Trial status
  const [trialInfo, setTrialInfo] = useState(null); // { is_trial, trial_expired, days_remaining }

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const [planRes, scheduleRes, historyRes, nutritionRes, summaryRes, streakRes, weightRes, weeklyRes, projectionRes] =
        await Promise.all([
          axios.get(`${API_URL}/api/diet/current`).catch(() => null),
          axios.get(`${API_URL}/api/calls/schedule`).catch(() => null),
          axios.get(`${API_URL}/api/calls/history?limit=5`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/nutrition/today`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/nutrition/summary`).catch(() => null),
          axios.get(`${API_URL}/api/nutrition/streak`).catch(() => ({ data: { streak: 0 } })),
          axios.get(`${API_URL}/api/nutrition/weight/history?days=30`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/nutrition/weekly`).catch(() => null),
          axios.get(`${API_URL}/api/diet/projection`).catch(() => null),
        ]);

      if (planRes?.data) setDietPlan(planRes.data);
      if (scheduleRes?.data) {
        setCallSchedule(scheduleRes.data);
        setNewCallTime(scheduleRes.data.preferred_time || "09:00");
        setNewCallFrequency(scheduleRes.data.frequency || "daily");
      }
      setCallHistory(historyRes?.data || []);
      setTodayNutrition(nutritionRes?.data || []);
      if (summaryRes?.data) setNutritionSummary(summaryRes.data);
      setStreak(streakRes?.data?.streak || 0);
      setWeightHistory(
        (weightRes?.data || []).map((w) => ({
          ...w,
          date: new Date(w.logged_at).toLocaleDateString("en-IN", { month: "short", day: "numeric" }),
        }))
      );
      if (weeklyRes?.data) setWeeklyNutrition(weeklyRes.data);
      if (projectionRes?.data) setGoalProjection(projectionRes.data);

      // Fetch trial status
      try {
        const token = await firebaseAuth.currentUser?.getIdToken();
        if (token) {
          const trial = await getTrialStatus(token);
          setTrialInfo(trial);
        }
      } catch (_) {
        // Non-critical — ignore
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegeneratePlan = async () => {
    setIsRegenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/diet/regenerate`);
      setDietPlan(response.data);
      toast.success("New diet plan generated!");
    } catch (error) {
      toast.error("Failed to regenerate diet plan");
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleLogFood = async (e) => {
    e.preventDefault();
    if (!foodForm.food_items) {
      toast.error("Please enter what you ate");
      return;
    }

    setIsLoggingFood(true);
    try {
      const payload = {
        ...foodForm,
        calories: foodForm.calories ? parseInt(foodForm.calories) : null,
        protein_g: foodForm.protein_g ? parseFloat(foodForm.protein_g) : null,
        carbs_g: foodForm.carbs_g ? parseFloat(foodForm.carbs_g) : null,
        fat_g: foodForm.fat_g ? parseFloat(foodForm.fat_g) : null,
      };
      await axios.post(`${API_URL}/api/nutrition/log`, payload);
      toast.success("Food logged successfully!");
      setFoodForm({ meal_type: "breakfast", food_items: "", calories: "", protein_g: "", carbs_g: "", fat_g: "", notes: "" });
      clearImage();
      const [nutritionRes, summaryRes, streakRes] = await Promise.all([
        axios.get(`${API_URL}/api/nutrition/today`),
        axios.get(`${API_URL}/api/nutrition/summary`),
        axios.get(`${API_URL}/api/nutrition/streak`),
      ]);
      setTodayNutrition(nutritionRes.data);
      setNutritionSummary(summaryRes.data);
      setStreak(streakRes.data?.streak || 0);
      const wRes = await axios.get(`${API_URL}/api/nutrition/weekly`).catch(() => null);
      if (wRes?.data) setWeeklyNutrition(wRes.data);
    } catch (error) {
      toast.error("Failed to log food");
    } finally {
      setIsLoggingFood(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    setDeletingLogId(logId);
    try {
      await axios.delete(`${API_URL}/api/nutrition/log/${logId}`);
      // Optimistic update
      setTodayNutrition((prev) => prev.filter((l) => l.id !== logId));
      toast.success("Meal removed");
      // Refresh summary & streak
      const [summaryRes, streakRes, weeklyRes] = await Promise.all([
        axios.get(`${API_URL}/api/nutrition/summary`).catch(() => null),
        axios.get(`${API_URL}/api/nutrition/streak`).catch(() => null),
        axios.get(`${API_URL}/api/nutrition/weekly`).catch(() => null),
      ]);
      if (summaryRes?.data) setNutritionSummary(summaryRes.data);
      if (streakRes?.data) setStreak(streakRes.data.streak || 0);
      if (weeklyRes?.data) setWeeklyNutrition(weeklyRes.data);
    } catch {
      toast.error("Failed to delete meal");
      // Re-fetch to restore true state
      const res = await axios.get(`${API_URL}/api/nutrition/today`).catch(() => null);
      if (res?.data) setTodayNutrition(res.data);
    } finally {
      setDeletingLogId(null);
    }
  };

  const startEditLog = (log) => {
    setEditingLogId(log.id);
    setEditForm({
      meal_type: log.meal_type,
      food_items: log.food_items,
      calories: log.calories ? String(log.calories) : "",
      notes: log.notes || "",
    });
  };

  const cancelEdit = () => {
    setEditingLogId(null);
    setEditForm({ meal_type: "", food_items: "", calories: "", notes: "" });
  };

  const handleSaveEdit = async (logId) => {
    if (!editForm.food_items) {
      toast.error("Food items cannot be empty");
      return;
    }
    setIsSavingEdit(true);
    try {
      const payload = {
        meal_type: editForm.meal_type,
        food_items: editForm.food_items,
        calories: editForm.calories ? parseInt(editForm.calories) : null,
        notes: editForm.notes || null,
      };
      const res = await axios.put(`${API_URL}/api/nutrition/log/${logId}`, payload);
      // Update the specific item in local state
      setTodayNutrition((prev) => prev.map((l) => (l.id === logId ? res.data : l)));
      toast.success("Meal updated!");
      cancelEdit();
      // Refresh summary
      const [summaryRes, weeklyRes] = await Promise.all([
        axios.get(`${API_URL}/api/nutrition/summary`).catch(() => null),
        axios.get(`${API_URL}/api/nutrition/weekly`).catch(() => null),
      ]);
      if (summaryRes?.data) setNutritionSummary(summaryRes.data);
      if (weeklyRes?.data) setWeeklyNutrition(weeklyRes.data);
    } catch {
      toast.error("Failed to update meal");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleLogWeight = async (e) => {
    e.preventDefault();
    if (!weightInput) {
      toast.error("Please enter your weight");
      return;
    }
    setIsLoggingWeight(true);
    try {
      await axios.post(`${API_URL}/api/nutrition/weight`, {
        weight_kg: parseFloat(weightInput),
      });
      toast.success("Weight logged!");
      setWeightInput("");
      const weightRes = await axios.get(`${API_URL}/api/nutrition/weight/history?days=30`);
      setWeightHistory(
        weightRes.data.map((w) => ({
          ...w,
          date: new Date(w.logged_at).toLocaleDateString("en-IN", { month: "short", day: "numeric" }),
        }))
      );
    } catch (error) {
      toast.error("Failed to log weight");
    } finally {
      setIsLoggingWeight(false);
    }
  };

  const handleUpdateSchedule = async () => {
    try {
      await axios.put(`${API_URL}/api/calls/schedule`, {
        preferred_call_time: newCallTime,
        call_frequency: newCallFrequency,
      });
      toast.success("Call schedule updated!");
      setSettingsOpen(false);
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to update schedule");
    }
  };

  const handleOptOut = async () => {
    try {
      await axios.post(`${API_URL}/api/calls/opt-out`);
      toast.success("Opted out of calls");
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to opt out");
    }
  };

  const handleOptIn = async () => {
    try {
      await axios.post(`${API_URL}/api/calls/opt-in`);
      toast.success("Opted back into calls");
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to opt in");
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You will still have access until the end of your current billing cycle.")) {
      return;
    }
    setIsCancellingSubscription(true);
    try {
      const token = await firebaseAuth.currentUser?.getIdToken();
      const res = await cancelSubscription(token);
      toast.success(res.message || "Subscription cancelled");
      setSettingsOpen(false);

      // Optimistically update UI to reflect cancellation
      if (user) {
        updateUser({ ...user, subscription_status: "cancelled" });
      }
    } catch (error) {
      toast.error(error.message || "Failed to cancel subscription");
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const handleInstantCall = async () => {
    if (!user?.id) return;
    setIsCalling(true);
    try {
      await axios.post(`${API_URL}/api/calls/trigger/${user.id}`);
      toast.success("Call initiated! Check your phone.");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to trigger call";
      toast.error(`Call Failed: ${errorMsg}`);
    } finally {
      setIsCalling(false);
    }
  };

  const handleEstimateCalories = async () => {
    if (!foodForm.food_items) return;
    setIsEstimatingCalories(true);
    try {
      const res = await axios.post(`${API_URL}/api/nutrition/estimate`, {
        food_items: foodForm.food_items,
      });
      const d = res.data;
      const updates = {};
      if (d.calories) updates.calories = String(d.calories);
      if (d.protein_g) updates.protein_g = String(d.protein_g);
      if (d.carbs_g) updates.carbs_g = String(d.carbs_g);
      if (d.fat_g) updates.fat_g = String(d.fat_g);
      if (Object.keys(updates).length > 0) {
        setFoodForm((f) => ({ ...f, ...updates }));
        const macroStr = d.protein_g ? ` (P:${d.protein_g}g C:${d.carbs_g}g F:${d.fat_g}g)` : "";
        toast.success(`AI estimate: ${d.calories} kcal${macroStr}`);
      } else {
        toast.error("Could not estimate — try describing the meal more specifically");
      }
    } catch {
      toast.error("AI estimate failed");
    } finally {
      setIsEstimatingCalories(false);
    }
  };

  // ── Image Upload Handlers ─────────────────────────────────────────────────
  const processImageFile = useCallback(async (file) => {
    if (!file) return;

    // Validate type
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/heic"];
    if (!allowed.includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, or WebP image");
      return;
    }

    // Validate size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large. Maximum is 5MB.");
      return;
    }

    // Set preview
    setImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setIsAnalyzingImage(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/api/nutrition/estimate-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      const updates = {};
      if (d.food_items) updates.food_items = d.food_items;
      if (d.calories) updates.calories = String(d.calories);
      if (d.protein_g) updates.protein_g = String(d.protein_g);
      if (d.carbs_g) updates.carbs_g = String(d.carbs_g);
      if (d.fat_g) updates.fat_g = String(d.fat_g);

      if (Object.keys(updates).length > 0) {
        setFoodForm((f) => ({ ...f, ...updates }));
        const macroStr = d.protein_g ? ` (P:${d.protein_g}g C:${d.carbs_g}g F:${d.fat_g}g)` : "";
        toast.success(`📷 Identified: ${d.food_items || "food"} — ${d.calories} kcal${macroStr}`);
      } else {
        toast.error("Could not identify food — try a clearer photo");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Image analysis failed";
      toast.error(detail);
    } finally {
      setIsAnalyzingImage(false);
    }
  }, []);

  const clearImage = useCallback(() => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
  }, [imagePreview]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      processImageFile(file);
    }
  }, [processImageFile]);

  const generateTimeSlots = () => {
    const slots = [];
    for (let h = 6; h <= 22; h++) {
      for (const m of [0, 30]) {
        // Create a local date for the time slot
        const localDate = new Date();
        localDate.setHours(h, m, 0, 0);

        // Get the UTC string "HH:MM"
        const utcHour = String(localDate.getUTCHours()).padStart(2, "0");
        const utcMin = String(localDate.getUTCMinutes()).padStart(2, "0");
        const utcValue = `${utcHour}:${utcMin}`;

        // Get the local display string "H:MM AM/PM"
        const ampm = h >= 12 ? "PM" : "AM";
        const displayH = h % 12 || 12;
        const mm = String(m).padStart(2, "0");

        slots.push({
          value: utcValue,
          label: `${displayH}:${mm} ${ampm}`,
        });
      }
    }
    return slots;
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // ── Profile Editing ─────────────────────────────────────────────────────────
  const openProfileModal = () => {
    const p = user?.profile || {};
    setProfileForm({
      name: user?.name || "",
      age: p.age ? String(p.age) : "",
      gender: p.gender || "",
      height_cm: p.height_cm ? String(p.height_cm) : "",
      weight_kg: p.weight_kg ? String(p.weight_kg) : "",
      target_weight_kg: p.target_weight_kg ? String(p.target_weight_kg) : "",
      diet_preference: p.diet_preference || "",
      activity_level: p.activity_level || "",
      health_conditions: p.health_conditions || [],
    });
    setRegeneratePlan(false);
    setProfileOpen(true);
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const payload = {};
      if (profileForm.name) payload.name = profileForm.name;
      if (profileForm.age) payload.age = parseInt(profileForm.age);
      if (profileForm.gender) payload.gender = profileForm.gender;
      if (profileForm.height_cm) payload.height_cm = parseFloat(profileForm.height_cm);
      if (profileForm.weight_kg) payload.weight_kg = parseFloat(profileForm.weight_kg);
      if (profileForm.target_weight_kg) payload.target_weight_kg = parseFloat(profileForm.target_weight_kg);
      if (profileForm.diet_preference) payload.diet_preference = profileForm.diet_preference;
      if (profileForm.activity_level) payload.activity_level = profileForm.activity_level;
      payload.health_conditions = profileForm.health_conditions || [];
      payload.regenerate_plan = regeneratePlan;

      const res = await axios.put(`${API_URL}/api/users/profile`, payload);
      toast.success(res.data.plan_regenerated
        ? "Profile saved & new diet plan generated!"
        : "Profile updated successfully!");
      if (res.data.user) updateUser(res.data.user);
      setProfileOpen(false);
      if (res.data.plan_regenerated) {
        const planRes = await axios.get(`${API_URL}/api/diet/current`).catch(() => null);
        if (planRes?.data) setDietPlan(planRes.data);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to update profile";
      toast.error(msg);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "Not set";
    const [utcHours, utcMinutes] = timeStr.split(":");

    // Create a date object with the UTC time
    const date = new Date();
    date.setUTCHours(parseInt(utcHours), parseInt(utcMinutes), 0, 0);

    // Get the local time formatting
    const localHour = date.getHours();
    const localMinute = String(date.getMinutes()).padStart(2, "0");
    const ampm = localHour >= 12 ? "PM" : "AM";
    const displayHour = localHour % 12 || 12;

    return `${displayHour}:${localMinute} ${ampm}`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    return new Date(isoString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalCalories = todayNutrition.reduce((sum, log) => sum + (log.calories || 0), 0);
  const calorieTarget = nutritionSummary?.calorie_target || null;
  const caloriePercent = calorieTarget ? Math.min((totalCalories / calorieTarget) * 100, 100) : null;
  const targetWeight = user?.profile?.target_weight_kg;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* ── Trial Expired Overlay ── */}
      {trialInfo?.is_trial && trialInfo?.trial_expired && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center space-y-5">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Your Free Trial Has Ended</h2>
            <p className="text-muted-foreground">
              Subscribe to continue using Nutri Saathi — your diet plans, AI coaching calls, and nutrition tracking are paused until you activate a subscription.
            </p>
            <Button
              onClick={() => navigate("/subscribe")}
              className="w-full bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl h-12 text-base btn-primary"
              data-testid="trial-expired-subscribe-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Subscribe Now — {SUBSCRIPTION_DISPLAY_PRICE}/month
            </Button>
          </div>
        </div>
      )}

      {/* ── Payment Failed (Halted) Overlay ── */}
      {user?.subscription_status === "halted" && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center space-y-5">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Payment Failed</h2>
            <p className="text-muted-foreground">
              Your subscription has been halted due to payment failures. Please update your payment method or subscribe again to restore access to your diet plans and AI coaching calls.
            </p>
            <Button
              onClick={() => navigate("/subscribe")}
              className="w-full bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl h-12 text-base btn-primary"
              data-testid="halted-subscribe-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Update Payment Method
            </Button>
          </div>
        </div>
      )}

      {/* ── Subscription Cancelled Overlay ── */}
      {/* Note: We only block access if they are cancelled AND past their subscription end date.
          Since the backend logic might not easily pass `subscription_end_date` evaluation to the frontend, 
          and `subscription_status` represents the immediate DB state, we check if cancelled and not in a trial.
          Ideally, we only lock them if new Date() > user.subscription_end_date. */}
      {user?.subscription_status === "cancelled" && (!user?.subscription_end_date || new Date() > new Date(user.subscription_end_date)) && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center space-y-5">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-orange-100 flex items-center justify-center">
              <LogOut className="w-8 h-8 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Subscription Ended</h2>
            <p className="text-muted-foreground">
              Your subscription has been cancelled and your billing cycle has ended. Subscribe again to restore full access.
            </p>
            <Button
              onClick={() => navigate("/subscribe")}
              className="w-full bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl h-12 text-base btn-primary"
              data-testid="cancelled-subscribe-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Subscribe Now
            </Button>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center">
              <img src="/logo.svg" alt="Nutri Saathi Logo" className="w-9 h-9 object-contain" />
            </div>
            <span className="font-bold text-lg text-foreground">Nutri Saathi</span>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-9 w-9 rounded-full flex items-center justify-center p-0 hover:bg-transparent focus-visible:ring-0"
                  data-testid="profile-dropdown-btn"
                >
                  <Avatar className="h-9 w-9 border border-[#D48C70]/20 hover:border-[#D48C70]/40 transition-colors">
                    <AvatarFallback className="bg-[#D48C70]/10 text-[#D48C70] font-bold">
                      {user?.name ? user.name[0].toUpperCase() : "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none text-foreground">{user?.name || "User"}</p>
                    <p className="text-xs leading-none text-muted-foreground">{user?.email || ""}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={openProfileModal}
                  className="cursor-pointer flex items-center gap-2 p-2 focus:bg-secondary focus:text-foreground"
                  data-testid="edit-profile-menu-item"
                >
                  <UserCog className="w-4 h-4 text-muted-foreground" />
                  <span>Edit Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setSettingsOpen(true)}
                  className="cursor-pointer flex items-center gap-2 p-2 focus:bg-secondary focus:text-foreground"
                  data-testid="settings-menu-item"
                >
                  <Settings className="w-4 h-4 text-muted-foreground" />
                  <span>Call Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer flex items-center gap-2 p-2 text-destructive focus:text-destructive focus:bg-destructive/10"
                  data-testid="logout-menu-item"
                >
                  <LogOut className="w-4 h-4 text-destructive" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* ── Edit Profile Dialog ── */}
            <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Profile</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  {/* Name */}
                  <div className="space-y-1">
                    <Label>Full Name</Label>
                    <Input
                      value={profileForm.name || ""}
                      onChange={(e) => setProfileForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Your name"
                      data-testid="profile-name-input"
                    />
                  </div>

                  {/* Age + Gender */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Age</Label>
                      <Input
                        type="number" min={10} max={100}
                        value={profileForm.age || ""}
                        onChange={(e) => setProfileForm((f) => ({ ...f, age: e.target.value }))}
                        placeholder="Age"
                        data-testid="profile-age-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Gender</Label>
                      <Select
                        value={profileForm.gender || ""}
                        onValueChange={(v) => setProfileForm((f) => ({ ...f, gender: v }))}
                      >
                        <SelectTrigger data-testid="profile-gender-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Height + Weight */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Height (cm)</Label>
                      <Input
                        type="number" min={100} max={250}
                        value={profileForm.height_cm || ""}
                        onChange={(e) => setProfileForm((f) => ({ ...f, height_cm: e.target.value }))}
                        placeholder="e.g. 170"
                        data-testid="profile-height-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Current Weight (kg)</Label>
                      <Input
                        type="number" min={30} max={300}
                        value={profileForm.weight_kg || ""}
                        onChange={(e) => setProfileForm((f) => ({ ...f, weight_kg: e.target.value }))}
                        placeholder="e.g. 72"
                        data-testid="profile-weight-input"
                      />
                    </div>
                  </div>

                  {/* Target weight + BMI guardrail */}
                  {(() => {
                    const h = parseFloat(profileForm.height_cm);
                    const tw = parseFloat(profileForm.target_weight_kg);
                    const bmiInfo = (h >= 100 && tw)
                      ? (() => {
                        const bmi = tw / Math.pow(h / 100, 2);
                        const minKg = Math.round(BMI_MIN * Math.pow(h / 100, 2) * 10) / 10;
                        const maxKg = Math.round(BMI_MAX * Math.pow(h / 100, 2) * 10) / 10;
                        return { bmi: Math.round(bmi * 10) / 10, valid: bmi >= BMI_MIN && bmi <= BMI_MAX, minKg, maxKg };
                      })()
                      : null;
                    return (
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <Label>Target Weight (kg)</Label>
                          {h >= 100 && (
                            <span className="text-xs text-muted-foreground">
                              Healthy: {Math.round(BMI_MIN * Math.pow(h / 100, 2) * 10) / 10}–{Math.round(BMI_MAX * Math.pow(h / 100, 2) * 10) / 10} kg
                            </span>
                          )}
                        </div>
                        <Input
                          type="number" min={30} max={300}
                          value={profileForm.target_weight_kg || ""}
                          onChange={(e) => setProfileForm((f) => ({ ...f, target_weight_kg: e.target.value }))}
                          placeholder="e.g. 68"
                          data-testid="profile-target-weight-input"
                          className={bmiInfo && !bmiInfo.valid ? "border-red-400" : bmiInfo?.valid ? "border-green-400" : ""}
                        />
                        {bmiInfo && (
                          <p className={`text-xs flex items-center gap-1 ${bmiInfo.valid ? "text-green-600" : "text-red-500"
                            }`}>
                            {bmiInfo.valid ? "✅" : "⚠️"}
                            {bmiInfo.valid
                              ? `BMI ${bmiInfo.bmi} — healthy ✓`
                              : `BMI ${bmiInfo.bmi} — outside healthy range. Choose ${bmiInfo.minKg}–${bmiInfo.maxKg} kg`
                            }
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Diet Preference */}
                  <div className="space-y-1">
                    <Label>Diet Preference</Label>
                    <Select
                      value={profileForm.diet_preference || ""}
                      onValueChange={(v) => setProfileForm((f) => ({ ...f, diet_preference: v }))}
                    >
                      <SelectTrigger data-testid="profile-diet-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="veg">Vegetarian</SelectItem>
                        <SelectItem value="non-veg">Non-Veg</SelectItem>
                        <SelectItem value="vegan">Vegan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Activity Level */}
                  <div className="space-y-1">
                    <Label>Activity Level</Label>
                    <Select
                      value={profileForm.activity_level || ""}
                      onValueChange={(v) => setProfileForm((f) => ({ ...f, activity_level: v }))}
                    >
                      <SelectTrigger data-testid="profile-activity-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sedentary">Sedentary</SelectItem>
                        <SelectItem value="light">Light (1-3 days/week)</SelectItem>
                        <SelectItem value="moderate">Moderate (3-5 days/week)</SelectItem>
                        <SelectItem value="active">Active (6-7 days/week)</SelectItem>
                        <SelectItem value="very_active">Very Active</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Health Conditions */}
                  <div className="space-y-2">
                    <Label>Health Conditions</Label>
                    <div className="flex flex-wrap gap-2">
                      {Array.from(new Set([...HEALTH_CONDITIONS, ...(profileForm.health_conditions || [])]))
                        .sort((a, b) => a === "None" ? 1 : b === "None" ? -1 : 0)
                        .map((condition) => {
                          const isSelected = (profileForm.health_conditions || []).includes(condition);
                          return (
                            <button
                              key={condition}
                              type="button"
                              onClick={() => {
                                setProfileForm((prev) => {
                                  const currentArr = prev.health_conditions || [];
                                  if (condition === "None") {
                                    if (currentArr.includes("None")) return { ...prev, health_conditions: [] };
                                    return { ...prev, health_conditions: ["None"] };
                                  }
                                  const current = currentArr.filter(c => c !== "None");
                                  if (current.includes(condition)) {
                                    return { ...prev, health_conditions: current.filter((c) => c !== condition) };
                                  } else {
                                    return { ...prev, health_conditions: [...current, condition] };
                                  }
                                });
                              }}
                              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${isSelected
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-secondary/50 text-foreground border-border hover:border-primary/50"
                                }`}
                            >
                              {condition}
                            </button>
                          );
                        })}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="Other condition..."
                        value={customCondition}
                        onChange={(e) => setCustomCondition(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!customCondition.trim()) return;
                            const cleanCond = customCondition.trim();
                            setProfileForm((prev) => {
                              const currentArr = prev.health_conditions || [];
                              if (currentArr.includes("None")) return prev;
                              if (!currentArr.includes(cleanCond)) {
                                return { ...prev, health_conditions: [...currentArr, cleanCond] };
                              }
                              return prev;
                            });
                            setCustomCondition("");
                          }
                        }}
                        className="text-sm"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (!customCondition.trim()) return;
                          const cleanCond = customCondition.trim();
                          setProfileForm((prev) => {
                            const currentArr = prev.health_conditions || [];
                            if (currentArr.includes("None")) return prev;
                            if (!currentArr.includes(cleanCond)) {
                              return { ...prev, health_conditions: [...currentArr, cleanCond] };
                            }
                            return prev;
                          });
                          setCustomCondition("");
                        }}
                        disabled={(profileForm.health_conditions || []).includes("None") || !customCondition.trim()}
                      >
                        Add Custom
                      </Button>
                    </div>
                  </div>

                  {/* Regenerate plan toggle */}
                  <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl">
                    <Checkbox
                      id="regen-plan"
                      checked={regeneratePlan}
                      onCheckedChange={setRegeneratePlan}
                      data-testid="profile-regen-checkbox"
                    />
                    <div>
                      <Label htmlFor="regen-plan" className="cursor-pointer font-medium">
                        Regenerate my diet plan
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        A new plan tailored to your updated goals will be created.
                      </p>
                    </div>
                  </div>

                  <Button
                    onClick={handleSaveProfile}
                    disabled={isSavingProfile}
                    className="w-full bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl"
                    data-testid="save-profile-btn"
                  >
                    {isSavingProfile ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" />Save Changes</>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Call Settings</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Preferred Call Time</Label>
                    <Select value={newCallTime} onValueChange={setNewCallTime}>
                      <SelectTrigger data-testid="settings-call-time-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {generateTimeSlots().map((slot) => (
                          <SelectItem key={slot.value} value={slot.value}>
                            {slot.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Call Frequency</Label>
                    <Select value={newCallFrequency} onValueChange={setNewCallFrequency}>
                      <SelectTrigger data-testid="settings-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="alternate">Alternate Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleUpdateSchedule}
                    className="w-full bg-primary hover:bg-primary/90"
                    data-testid="save-settings-btn"
                  >
                    Save Changes
                  </Button>
                  {callSchedule?.status === "active" ? (
                    <Button
                      variant="outline"
                      onClick={handleOptOut}
                      className="w-full text-destructive border-destructive hover:bg-destructive/10"
                      data-testid="opt-out-btn"
                    >
                      <PhoneOff className="w-4 h-4 mr-2" />
                      Opt Out of Calls
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleOptIn}
                      className="w-full"
                      data-testid="opt-in-btn"
                    >
                      <PhoneCall className="w-4 h-4 mr-2" />
                      Opt Back In
                    </Button>
                  )}

                  {/* Cancel Subscription Button — hide for trial users (no Razorpay sub to cancel) */}
                  {(user?.subscription_status === "active") && !trialInfo?.is_trial && (
                    <div className="pt-4 mt-2 border-t border-border">
                      <Button
                        variant="ghost"
                        onClick={handleCancelSubscription}
                        disabled={isCancellingSubscription}
                        className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 justify-start px-2"
                      >
                        {isCancellingSubscription ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <LogOut className="w-4 h-4 mr-2" />
                        )}
                        Cancel Subscription
                      </Button>
                      <p className="text-[10px] text-muted-foreground mt-2 px-2 text-center">
                        Cancelling prevents future charges. You will keep access until the end of your billing cycle.
                      </p>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* ── Active Trial Banner ── */}
      {trialInfo?.is_trial && !trialInfo?.trial_expired && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
          <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-green-600" />
            <span className="text-green-800 font-medium">
              {trialInfo.days_remaining > 1
                ? `${trialInfo.days_remaining} days left in your free trial`
                : trialInfo.days_remaining === 1
                  ? "Last day of your free trial!"
                  : "Your free trial ends today!"}
            </span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Hello, {user?.name?.split(" ")[0] || "there"}!
            </h1>
            <p className="text-muted-foreground">Here's your wellness overview</p>
          </div>
          <Button
            onClick={() => navigate("/chat")}
            className="bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-full btn-primary"
            data-testid="chat-main-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Chat with AI
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Goal */}
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Goal</p>
                  <p className="font-semibold text-foreground capitalize">
                    {user?.profile?.primary_goal === "weight_loss" ? "Weight Loss"
                      : user?.profile?.primary_goal === "weight_gain" ? "Weight Gain"
                        : user?.profile?.primary_goal?.replace(/_/g, " ") || "Not set"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Today's Calories with target bar */}
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Utensils className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-muted-foreground">Today's Calories</p>
                  <p className="font-semibold text-foreground">
                    {totalCalories}
                    {calorieTarget && (
                      <span className="text-sm font-normal text-muted-foreground"> / {calorieTarget}</span>
                    )}
                    <span className="text-xs text-muted-foreground"> kcal</span>
                  </p>
                </div>
              </div>
              {calorieTarget && (
                <Progress
                  value={caloriePercent}
                  className="h-1.5"
                  style={{
                    "--progress-color":
                      caloriePercent >= 100 ? "#ef4444" : "#D48C70",
                  }}
                />
              )}
            </CardContent>
          </Card>

          {/* Next Call + Call Now */}
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <PhoneCall className="w-5 h-5 text-[#D48C70]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Next Call</p>
                  <p className="font-semibold text-foreground">
                    {callSchedule?.status === "active"
                      ? formatTime(callSchedule?.preferred_time)
                      : "Opted out"}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={handleInstantCall}
                  disabled={isCalling}
                  data-testid="call-now-btn"
                  className="bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-full px-3 text-xs font-semibold"
                >
                  {isCalling ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Phone className="w-3.5 h-3.5 mr-1" />
                  )}
                  {isCalling ? "Calling..." : "Call Now"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* 🔥 Streak */}
          <Card className="card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Log Streak</p>
                  {streak > 0 ? (
                    <p className="font-semibold text-foreground">{streak} day{streak !== 1 ? "s" : ""} 🔥</p>
                  ) : (
                    <p className="font-semibold text-muted-foreground">Start today!</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Dashboard Tabs */}
        <Tabs defaultValue="diet" className="space-y-4">
          <TabsList className="bg-secondary/50 p-1">
            <TabsTrigger value="diet" data-testid="diet-tab">Diet Plan</TabsTrigger>
            <TabsTrigger value="nutrition" data-testid="nutrition-tab">Nutrition Log</TabsTrigger>
            <TabsTrigger value="weight" data-testid="weight-tab">Weight</TabsTrigger>
            <TabsTrigger value="calls" data-testid="calls-tab">Call History</TabsTrigger>
          </TabsList>

          {/* Diet Plan Tab */}
          <TabsContent value="diet">
            {goalProjection && (
              <Card className="border-border/50 mb-6 bg-gradient-to-br from-card to-secondary/20">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#D48C70]" />
                      {goalProjection.goal === "weight_gain" ? "Gain Timeline" : "Your Goal Timeline"}
                    </CardTitle>
                    {goalProjection.weeks_to_goal === 0 ? (
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full animate-bounce">
                        🎉 GOAL REACHED!
                      </span>
                    ) : (
                      <span className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Est. by {new Date(goalProjection.target_date).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Current</p>
                      <p className="text-xl font-bold">{goalProjection.current_weight} kg</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Target</p>
                      <p className="text-xl font-bold">{goalProjection.target_weight} kg</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Remaining</p>
                      <p className="text-xl font-bold">{goalProjection.weeks_to_goal} weeks</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Weekly Rate</p>
                      <p className="text-xl font-bold text-[#D48C70]">~{goalProjection.weekly_change_kg} kg</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground font-medium">Journey Progress</span>
                      <span className="font-bold text-foreground">{goalProjection.pct_complete}%</span>
                    </div>
                    <Progress
                      value={goalProjection.pct_complete}
                      className="h-3 bg-secondary/50"
                      indicatorClassName={goalProjection.pct_complete >= 80 ? "bg-green-500" : "bg-[#D48C70]"}
                    />
                    <p className="text-xs text-center text-muted-foreground pt-1 italic">
                      {goalProjection.weeks_to_goal === 0
                        ? "You've crushed your target! Maintaining now."
                        : `You're on track with a ${Math.abs(goalProjection.calorie_delta_per_day)} kcal daily ${goalProjection.calorie_delta_per_day < 0 ? "deficit" : "surplus"}.`}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-border/50">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-xl">Your Diet Plan</CardTitle>
                  <CardDescription>
                    Personalized for your goals • Last updated:{" "}
                    {dietPlan ? formatDate(dietPlan.created_at) : "N/A"}
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  onClick={handleRegeneratePlan}
                  disabled={isRegenerating}
                  className="rounded-xl"
                  data-testid="regenerate-plan-btn"
                >
                  {isRegenerating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Regenerate
                </Button>
              </CardHeader>
              <CardContent>
                {dietPlan ? (
                  <ScrollArea className="h-[500px] pr-4">
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {dietPlan.plan_content}
                      </ReactMarkdown>
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-12">
                    <Utensils className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No diet plan found</p>
                    <Button
                      onClick={handleRegeneratePlan}
                      className="mt-4 bg-[#D48C70] hover:bg-[#c67d61] text-white"
                      data-testid="generate-first-plan-btn"
                    >
                      Generate Your Plan
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Nutrition Log Tab */}
          <TabsContent value="nutrition">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Log Food Form */}
              <Card
                className={`border-border/50 transition-colors ${isDragOver ? "border-[#D48C70] border-2 bg-[#D48C70]/5" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Log Your Meal
                  </CardTitle>
                  {calorieTarget && (
                    <CardDescription>
                      <span className={totalCalories >= calorieTarget ? "text-red-500 font-medium" : ""}>
                        {totalCalories} / {calorieTarget} kcal today
                      </span>
                      {totalCalories >= calorieTarget && " — Target reached!"}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogFood} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Meal Type</Label>
                      <Select
                        value={foodForm.meal_type}
                        onValueChange={(v) => setFoodForm((f) => ({ ...f, meal_type: v }))}
                      >
                        <SelectTrigger data-testid="log-meal-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="breakfast">Breakfast</SelectItem>
                          <SelectItem value="lunch">Lunch</SelectItem>
                          <SelectItem value="dinner">Dinner</SelectItem>
                          <SelectItem value="snack">Snack</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* ── 📷 Food Image Upload ── */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5">
                        <Camera className="w-3.5 h-3.5" />
                        Scan Food Photo
                      </Label>
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/heic"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => processImageFile(e.target.files?.[0])}
                        data-testid="food-image-input"
                      />

                      {!imagePreview ? (
                        /* Drop zone / upload trigger */
                        <button
                          type="button"
                          onClick={() => imageInputRef.current?.click()}
                          disabled={isAnalyzingImage}
                          className={`
                            w-full rounded-xl border-2 border-dashed transition-all duration-200
                            flex flex-col items-center justify-center gap-2 py-5
                            ${isDragOver
                              ? "border-[#D48C70] bg-[#D48C70]/10 scale-[1.01]"
                              : "border-border/60 hover:border-[#D48C70]/50 hover:bg-[#D48C70]/5"
                            }
                          `}
                          data-testid="food-image-upload-btn"
                        >
                          <div className="w-10 h-10 rounded-full bg-[#D48C70]/10 flex items-center justify-center">
                            <Camera className="w-5 h-5 text-[#D48C70]" />
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-foreground">
                              Take a photo or upload
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              AI will identify food & estimate nutrition
                            </p>
                          </div>
                        </button>
                      ) : (
                        /* Image preview with analyzing state */
                        <div className="relative rounded-xl overflow-hidden border border-border/50">
                          <img
                            src={imagePreview}
                            alt="Food preview"
                            className={`w-full max-h-[160px] object-cover ${isAnalyzingImage ? "opacity-60" : ""}`}
                          />
                          {isAnalyzingImage && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 backdrop-blur-[2px]">
                              <Loader2 className="w-8 h-8 text-white animate-spin mb-2" />
                              <p className="text-white text-sm font-medium">Analyzing food...</p>
                            </div>
                          )}
                          {!isAnalyzingImage && (
                            <button
                              type="button"
                              onClick={clearImage}
                              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                              data-testid="clear-image-btn"
                            >
                              <X className="w-4 h-4 text-white" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>What did you eat?</Label>
                      <Input
                        placeholder="e.g., 2 rotis, dal, sabzi"
                        value={foodForm.food_items}
                        onChange={(e) =>
                          setFoodForm((f) => ({ ...f, food_items: e.target.value }))
                        }
                        className="input-wellness"
                        data-testid="log-food-items-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Calories (optional)</Label>
                        <button
                          type="button"
                          onClick={handleEstimateCalories}
                          disabled={isEstimatingCalories || !foodForm.food_items}
                          className="text-xs text-[#D48C70] hover:text-[#c67d61] font-medium disabled:opacity-40 flex items-center gap-1 transition-colors"
                          data-testid="ai-estimate-btn"
                        >
                          {isEstimatingCalories ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            "🤖"
                          )}
                          {isEstimatingCalories ? "Estimating..." : "AI Estimate"}
                        </button>
                      </div>
                      <Input
                        type="number"
                        placeholder="Estimated calories"
                        value={foodForm.calories}
                        onChange={(e) =>
                          setFoodForm((f) => ({ ...f, calories: e.target.value }))
                        }
                        className="input-wellness"
                        data-testid="log-calories-input"
                      />
                    </div>
                    {/* Macro inputs — compact 3-col row */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Macros (optional — filled by AI Estimate)</Label>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="flex flex-col gap-1">
                          <Label className="text-[11px] text-muted-foreground font-medium px-0.5">Protein (g)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Protein"
                            value={foodForm.protein_g}
                            onChange={(e) => setFoodForm((f) => ({ ...f, protein_g: e.target.value }))}
                            className="input-wellness text-xs h-8"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Label className="text-[11px] text-muted-foreground font-medium px-0.5">Carbs (g)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Carbs"
                            value={foodForm.carbs_g}
                            onChange={(e) => setFoodForm((f) => ({ ...f, carbs_g: e.target.value }))}
                            className="input-wellness text-xs h-8"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Label className="text-[11px] text-muted-foreground font-medium px-0.5">Fat (g)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="Fat"
                            value={foodForm.fat_g}
                            onChange={(e) => setFoodForm((f) => ({ ...f, fat_g: e.target.value }))}
                            className="input-wellness text-xs h-8"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes (optional)</Label>
                      <Input
                        placeholder="Any additional notes"
                        value={foodForm.notes}
                        onChange={(e) =>
                          setFoodForm((f) => ({ ...f, notes: e.target.value }))
                        }
                        className="input-wellness"
                        data-testid="log-notes-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl"
                      disabled={isLoggingFood}
                      data-testid="log-food-submit-btn"
                    >
                      {isLoggingFood ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      )}
                      Log Meal
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Today's Log */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Today's Log
                  </CardTitle>
                  <CardDescription>
                    {calorieTarget ? (
                      <div className="space-y-1 mt-1">
                        <div className="flex justify-between text-xs">
                          <span>{totalCalories} kcal logged</span>
                          <span>{calorieTarget} kcal target</span>
                        </div>
                        <Progress value={caloriePercent} className="h-2" />
                      </div>
                    ) : (
                      `Total: ${totalCalories} calories logged`
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {todayNutrition.length > 0 ? (
                    <div className="space-y-3">
                      {todayNutrition.map((log) => (
                        <div key={log.id}>
                          {editingLogId === log.id ? (
                            /* ─── Inline edit form ─── */
                            <div className="p-3 bg-secondary/40 rounded-xl space-y-3 border border-border/50">
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <Label className="text-xs">Meal Type</Label>
                                  <Select
                                    value={editForm.meal_type}
                                    onValueChange={(v) => setEditForm((f) => ({ ...f, meal_type: v }))}
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="breakfast">Breakfast</SelectItem>
                                      <SelectItem value="lunch">Lunch</SelectItem>
                                      <SelectItem value="dinner">Dinner</SelectItem>
                                      <SelectItem value="snack">Snack</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Calories</Label>
                                  <Input
                                    type="number"
                                    className="h-8 text-xs"
                                    placeholder="kcal"
                                    value={editForm.calories}
                                    onChange={(e) => setEditForm((f) => ({ ...f, calories: e.target.value }))}
                                  />
                                </div>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">What did you eat?</Label>
                                <Input
                                  className="h-8 text-xs"
                                  value={editForm.food_items}
                                  onChange={(e) => setEditForm((f) => ({ ...f, food_items: e.target.value }))}
                                />
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-xs"
                                  onClick={cancelEdit}
                                >
                                  <X className="w-3 h-3 mr-1" /> Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-7 text-xs bg-[#D48C70] hover:bg-[#c67d61] text-white"
                                  onClick={() => handleSaveEdit(log.id)}
                                  disabled={isSavingEdit}
                                >
                                  {isSavingEdit ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Save className="w-3 h-3 mr-1" />
                                  )}
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            /* ─── Normal meal row ─── */
                            <div className="flex items-start gap-3 p-3 bg-secondary/30 rounded-xl group">
                              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Utensils className="w-5 h-5 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground capitalize">
                                  {log.meal_type}
                                </p>
                                <p className="text-sm text-muted-foreground truncate">
                                  {log.food_items}
                                </p>
                                {log.calories && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    <span className="text-xs text-primary font-medium">{log.calories} kcal</span>
                                    {log.protein_g && (
                                      <span className="text-xs bg-blue-100 text-blue-700 rounded px-1 font-medium">P:{log.protein_g}g</span>
                                    )}
                                    {log.carbs_g && (
                                      <span className="text-xs bg-amber-100 text-amber-700 rounded px-1 font-medium">C:{log.carbs_g}g</span>
                                    )}
                                    {log.fat_g && (
                                      <span className="text-xs bg-rose-100 text-rose-700 rounded px-1 font-medium">F:{log.fat_g}g</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-muted-foreground mr-1">
                                  {new Date(log.logged_at).toLocaleTimeString("en-IN", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                                {/* Edit button */}
                                <button
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground"
                                  onClick={() => startEditLog(log)}
                                  title="Edit meal"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                {/* Delete button */}
                                <button
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-red-100 text-muted-foreground hover:text-red-500"
                                  onClick={() => handleDeleteLog(log.id)}
                                  disabled={deletingLogId === log.id}
                                  title="Delete meal"
                                >
                                  {deletingLogId === log.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Utensils className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No meals logged today</p>
                      <p className="text-sm text-muted-foreground">
                        Start tracking your nutrition!
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Weekly Calorie Bar Chart */}
            {weeklyNutrition && (
              <Card className="border-border/50 mt-6">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Weekly Calories
                  </CardTitle>
                  {weeklyNutrition.calorie_target && (
                    <CardDescription>
                      Daily target: {weeklyNutrition.calorie_target} kcal
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {weeklyNutrition.days?.some((d) => d.total_calories > 0) ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={weeklyNutrition.days} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "8px",
                            fontSize: "12px",
                          }}
                          formatter={(value) => [`${value} kcal`, "Calories"]}
                        />
                        {weeklyNutrition.calorie_target && (
                          <ReferenceLine
                            y={weeklyNutrition.calorie_target}
                            stroke="#D48C70"
                            strokeDasharray="6 3"
                            label={{ value: "Target", position: "right", fontSize: 11, fill: "#D48C70" }}
                          />
                        )}
                        <Bar dataKey="total_calories" radius={[6, 6, 0, 0]}>
                          {weeklyNutrition.days.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={
                                weeklyNutrition.calorie_target && entry.total_calories >= weeklyNutrition.calorie_target
                                  ? "#22c55e"
                                  : entry.total_calories > 0
                                    ? "#D48C70"
                                    : "hsl(var(--border))"
                              }
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[220px] text-center">
                      <Calendar className="w-10 h-10 text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">No meals logged this week</p>
                      <p className="text-sm text-muted-foreground">Start logging to see your weekly chart</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Weight Tracking Tab */}
          <TabsContent value="weight">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Log Weight */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Weight className="w-5 h-5" />
                    Log Your Weight
                  </CardTitle>
                  <CardDescription>
                    Target: {targetWeight ? `${targetWeight} kg` : "Not set"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogWeight} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Current Weight (kg)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="e.g., 74.5"
                        value={weightInput}
                        onChange={(e) => setWeightInput(e.target.value)}
                        className="input-wellness"
                        data-testid="log-weight-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl"
                      disabled={isLoggingWeight}
                      data-testid="log-weight-submit-btn"
                    >
                      {isLoggingWeight ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      )}
                      Log Weight
                    </Button>
                  </form>

                  {/* Latest reading */}
                  {weightHistory.length > 0 && (
                    <div className="mt-6 p-4 bg-secondary/30 rounded-xl">
                      <p className="text-sm text-muted-foreground">Latest Reading</p>
                      <p className="text-2xl font-bold text-foreground">
                        {weightHistory[weightHistory.length - 1].weight_kg} kg
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {weightHistory[weightHistory.length - 1].date}
                      </p>
                      {targetWeight && (
                        <p className={`text-sm mt-1 font-medium ${weightHistory[weightHistory.length - 1].weight_kg <= targetWeight
                          ? "text-green-600"
                          : "text-[#D48C70]"
                          }`}>
                          {weightHistory[weightHistory.length - 1].weight_kg <= targetWeight
                            ? "🎉 Target reached!"
                            : `${(weightHistory[weightHistory.length - 1].weight_kg - targetWeight).toFixed(1)} kg to go`}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Weight Chart */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Weight Progress (30 days)
                  </CardTitle>
                  {targetWeight && (
                    <CardDescription>
                      Dotted line = target ({targetWeight} kg)
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {weightHistory.length >= 2 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={weightHistory} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                          tickLine={false}
                        />
                        <YAxis
                          domain={["auto", "auto"]}
                          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "8px",
                            fontSize: "12px",
                          }}
                          formatter={(value) => [`${value} kg`, "Weight"]}
                        />
                        {targetWeight && (
                          <ReferenceLine
                            y={targetWeight}
                            stroke="#D48C70"
                            strokeDasharray="6 3"
                            label={{ value: "Target", position: "right", fontSize: 11, fill: "#D48C70" }}
                          />
                        )}
                        <Line
                          type="monotone"
                          dataKey="weight_kg"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2.5}
                          dot={{ r: 4, fill: "hsl(var(--primary))", strokeWidth: 0 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[250px] text-center">
                      <Weight className="w-12 h-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Log at least 2 weight entries</p>
                      <p className="text-sm text-muted-foreground">to see your progress chart</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Call History Tab */}
          <TabsContent value="calls">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Call History
                  <span className="ml-auto text-sm font-normal text-muted-foreground">
                    {callHistory.length} call{callHistory.length !== 1 ? "s" : ""}
                  </span>
                </CardTitle>
                <CardDescription>
                  Your recent accountability call logs
                </CardDescription>
              </CardHeader>
              <CardContent>
                {callHistory.length > 0 ? (
                  <div className="space-y-4">
                    {callHistory.map((call) => {
                      const isReached = call.status !== "couldn't_reach";
                      return (
                        <div
                          key={call.id}
                          className={`p-4 rounded-xl space-y-3 ${isReached ? 'bg-secondary/30' : 'bg-red-50/50 dark:bg-red-950/20'}`}
                        >
                          {/* Header row */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isReached ? 'bg-green-100' : 'bg-red-100'}`}>
                                {isReached ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                                ) : (
                                  <PhoneOff className="w-4 h-4 text-red-500" />
                                )}
                              </div>
                              <div>
                                <span className="font-medium text-foreground text-sm">
                                  {formatDate(call.call_time)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isReached && (
                                <span className="text-xs text-muted-foreground">
                                  {Math.floor(call.duration_seconds / 60)}m {Math.round(call.duration_seconds % 60)}s
                                </span>
                              )}
                              <span className={`inline-block text-xs px-2 py-1 rounded-full font-medium ${isReached
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-600'
                                }`}>
                                {isReached ? "Connected" : "Couldn't reach"}
                              </span>
                            </div>
                          </div>

                          {/* Summary — only for connected calls */}
                          {isReached && call.summary && (
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {call.summary}
                            </p>
                          )}

                          {/* Couldn't reach subtitle */}
                          {!isReached && (
                            <p className="text-xs text-red-500/80 italic">
                              {call.ended_reason === "customer-busy"
                                ? "Your line was busy"
                                : "No answer — we'll try again next time"}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 space-y-4">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-orange-50 flex items-center justify-center">
                      <PhoneCall className="w-8 h-8 text-[#D48C70]" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">No calls yet — let's start!</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Riya will hold you accountable and review your meals.
                      </p>
                    </div>
                    {callSchedule?.status === "active" && callSchedule?.preferred_time && (
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full text-sm">
                        <Clock className="w-3.5 h-3.5 text-primary" />
                        <span className="text-primary font-medium">
                          Next scheduled call at {formatTime(callSchedule.preferred_time)}
                        </span>
                      </div>
                    )}
                    <div>
                      <Button
                        onClick={handleInstantCall}
                        disabled={isCalling}
                        className="bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-full"
                      >
                        {isCalling ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Phone className="w-4 h-4 mr-2" />
                        )}
                        {isCalling ? "Calling..." : "Call Riya Now"}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">Get instant accountability</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Disclaimer */}
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm text-amber-800">
            <strong>Disclaimer:</strong> This is not medical advice. Please consult a
            healthcare professional for personalized medical guidance.
          </p>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden mobile-nav p-2">
        <div className="flex justify-around">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex flex-col items-center gap-1 p-2 text-primary"
            data-testid="mobile-nav-home"
          >
            <img src="/logo.svg" alt="Home" className="w-5 h-5 object-contain" />
            <span className="text-xs">Home</span>
          </button>
          <button
            onClick={() => navigate("/chat")}
            className="flex flex-col items-center gap-1 p-2 text-muted-foreground"
            data-testid="mobile-nav-chat"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-xs">Chat</span>
          </button>
          <button
            onClick={() => setSettingsOpen(true)}
            className="flex flex-col items-center gap-1 p-2 text-muted-foreground"
            data-testid="mobile-nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs">Settings</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default DashboardPage;
